import React, { useState } from 'react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';
import { useTheme } from "./theme-provider";
import emailjs from '@emailjs/browser';
import logger from '../../utils/logger';

// EmailJS is already initialized in EarlyAccessModal.js

export default function CtaSection() {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Get current date
    const currentDate = new Date().toLocaleString();
    
    // Send email using EmailJS
    emailjs.send(
      'service_ninyd0i', // Your EmailJS service ID
      'template_faawove', // Your EmailJS template ID for general signup
      {
        email: email,
        website: website || "Not provided",
        date: currentDate
      }
    )
    .then(() => {
      setEmail('');
      setWebsite('');
      setIsSubmitted(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
      
      setIsLoading(false);
    })
    .catch((error) => {
      logger.error("EmailJS error:", error);
      setIsLoading(false);
      // You might want to add error handling UI here
    });
  };
  
  const features = [
    "Lifetime Pro+ Access at one time payment",
    "On-call audit session for your use-case",
    "Free Knowledge Base & Chatbot integration",
    "Access to all beta features",
    "Top priority for your feature requests"
  ];
  
  return (
    <section className={`py-20 relative ${
      isDarkMode ? 'bg-gradient-to-b from-gray-950 to-gray-900' : ''
    }`}>
      {/* Dark mode specific glow effects */}
      {isDarkMode && (
        <>
          <div className="absolute top-1/3 left-1/4 w-1/2 h-64 bg-purple-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/4 w-1/2 h-64 bg-blue-600/10 rounded-full blur-3xl"></div>
        </>
      )}
      
      <div className="container px-4 md:px-6 relative z-10">
        {/* Original CTA header section */}
        <div className="flex flex-col items-center justify-center space-y-4 text-center mb-8">
          <div className="space-y-2">
            <h2 className={`text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl ${
              isDarkMode ? 'text-white' : ''
            }`}>
              Lifetime access deal for early adopters!
            </h2>
            <p className={`max-w-[900px] ${
              isDarkMode ? 'text-gray-300' : 'text-gray-500'
            } md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed`}>
              Join our exclusive early access program to be among the first to experience the product before launch and influence feature development based on your priorities.
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Lifetime access offer valid for limited slots.
            </p>
          </div>
        </div>
        
        {/* CTA form section with conditional styling for dark mode */}
        <div className={`relative overflow-hidden rounded-xl ${
          isDarkMode 
            ? 'border border-gray-800 bg-gradient-to-br from-gray-900/95 via-gray-900/80 to-gray-900/90 backdrop-blur-xl'
            : 'border border-white/20 bg-gradient-to-br from-black/95 via-black/80 to-black/90 backdrop-blur-xl'
        } p-6 shadow-xl`}>
          <div className={`absolute inset-0 ${
            isDarkMode
              ? 'bg-gradient-to-br from-purple-900/30 via-gray-900/80 to-blue-900/30'
              : 'bg-gradient-to-br from-purple-900/20 via-black/80 to-blue-900/20'
          } -z-10`}></div>
          
          {/* Animated particles */}
          <div className="absolute inset-0 -z-5">
            {Array.from({ length: 20 }).map((_, i) => (
              <div 
                key={i}
                className={`absolute rounded-full ${isDarkMode ? 'bg-white/10' : 'bg-white/5'}`}
                style={{
                  width: `${Math.random() * 6 + 2}px`,
                  height: `${Math.random() * 6 + 2}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  animation: `float ${Math.random() * 10 + 5}s infinite ease-in-out`,
                }}
              ></div>
            ))}
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div>
                <div className={`inline-flex items-center ${
                  isDarkMode ? 'bg-purple-900/30' : 'bg-white/10'
                } backdrop-blur-md rounded-full px-4 py-1.5 mb-6`}>
                  <div className="w-2 h-2 rounded-full bg-green-400 mr-2 animate-pulse"></div>
                  <span className="text-sm font-medium text-white/90">Pre-Launch Limited Time Offer</span>
                </div>
                
                <h3 className="text-2xl font-bold mb-6 leading-tight text-white">
                  Join our early access program
                </h3>
                
                <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-300'} mb-8`}>
                  Be among the first to transform your customer experience with SmushLabs!
                </p>
                
                <ul className="space-y-3 mb-8">
                  {features.map((feature, index) => (
                    <li key={index} className={`flex items-center ${isDarkMode ? 'text-gray-100' : 'text-gray-200'}`}>
                      <CheckCircle size={18} className={`${isDarkMode ? 'text-purple-400' : 'text-green-400'} mr-3`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className={`${
                isDarkMode
                  ? 'bg-gray-800/40 border border-gray-700/50'
                  : 'bg-white/5 border border-white/10'
              } backdrop-blur-md rounded-2xl p-6 shadow-lg`}>
                <div className="flex items-center mb-6">
                  <div className={`w-10 h-10 rounded-full ${
                    isDarkMode
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600'
                      : 'bg-gradient-to-r from-purple-500 to-blue-500'
                  } flex items-center justify-center mr-3`}>
                    <MessageSquare size={20} className="text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Get Started Today</h3>
                </div>
                
                {isSubmitted ? (
                  <div className={`${
                    isDarkMode ? 'bg-green-500/10' : 'bg-green-500/20'
                  } rounded-xl p-6 text-center backdrop-blur-sm`}>
                    <CheckCircle size={48} className="text-green-400 mx-auto mb-3" />
                    <h4 className="text-xl font-medium text-white mb-2">Thank You!</h4>
                    <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-300'}`}>
                      We've added you to our early access list. You'll be among the first to know when we launch!
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="cta-website" className={`block text-sm font-medium ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-300'
                      } mb-1`}>
                        Website URL
                      </label>
                      <Input
                        id="cta-website"
                        type="url"
                        placeholder="https://yourcompany.com"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        className={`w-full ${
                          isDarkMode
                            ? 'bg-gray-800/50 border-gray-700 focus:ring-purple-600/50'
                            : 'bg-white/10 border-white/20 focus:ring-purple-500/50'
                        } backdrop-blur-md text-white placeholder:text-gray-400 focus:ring-2 focus:border-transparent transition-all`}
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="cta-email" className={`block text-sm font-medium ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-300'
                      } mb-1`}>
                        Email Address
                      </label>
                      <Input
                        id="cta-email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className={`w-full ${
                          isDarkMode
                            ? 'bg-gray-800/50 border-gray-700 focus:ring-purple-600/50'
                            : 'bg-white/10 border-white/20 focus:ring-purple-500/50'
                        } backdrop-blur-md text-white placeholder:text-gray-400 focus:ring-2 focus:border-transparent transition-all`}
                      />
                    </div>
                    
                    <Button 
                      type="submit"
                      disabled={isLoading}
                      className={`w-full ${
                        isDarkMode
                          ? 'bg-gradient-to-r from-purple-600 to-blue-600 shadow-purple-600/20'
                          : 'bg-gradient-to-r from-purple-500 to-blue-500 shadow-purple-500/20'
                      } hover:opacity-90 text-white rounded-xl h-12 transition-all shadow-md`}
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                          Processing...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          Get Started Now
                          <ArrowRight size={16} className="ml-2" />
                        </div>
                      )}
                    </Button>
                    
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} text-center`}>
                      By signing up, you agree to our Terms of Service and Privacy Policy.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
